import { authGuard } from "@auth0/auth0-vue";
import { watch } from "vue";
import { createRouter, createWebHistory, type RouteLocationNormalizedGeneric } from "vue-router";

import { i18n } from "./i18n.ts";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      redirect: {
        name: "settings",
        // Remove the auth0 “code” and “state” query parameters from the URL as they're no longer needed.
        query: {},
      },
    },
    {
      path: "/settings",
      name: "settings",
      components: {
        default: () => import("@/views/ProfileSettings.vue"),
        Header: () => import("@/components/header/Header.vue"),
        Footer: () => import("@/components/footer/MainFooter.vue"),
      },
      meta: { title: "profileSettings" },
      beforeEnter: [authGuard],
    },
    {
      path: "/deleted",
      name: "accountDeleted",
      components: {
        default: () => import("@/views/AccountDeleted.vue"),
        Header: () => import("@/components/header/Header.vue"),
        Footer: () => import("@/components/footer/MainFooter.vue"),
      },
      meta: { title: "accountDeleted" },
    },
    {
      path: "/terms",
      name: "termsAndConditions",
      components: {
        default: () => import("@/views/TermsAndConditions.vue"),
        Header: () => import("@/components/header/Header.vue"),
        Footer: () => import("@/components/footer/MainFooter.vue"),
      },
      meta: { title: "termsAndConditions" },
      props: { Header: { hasBackButton: true } },
    },
    {
      path: "/privacy-website",
      name: "dataPrivacyStatementWeb",
      components: {
        default: () => import("@/views/PrivacyWebsite.vue"),
        Header: () => import("@/components/header/Header.vue"),
        Footer: () => import("@/components/footer/MainFooter.vue"),
      },
      meta: { title: "privacyWebsite" },
      props: { Header: { hasBackButton: true } },
    },
    {
      path: "/privacy-service",
      name: "dataPrivacyStatementService",
      components: {
        default: () => import("@/views/PrivacyService.vue"),
        Header: () => import("@/components/header/Header.vue"),
        Footer: () => import("@/components/footer/MainFooter.vue"),
      },
      meta: { title: "privacyService" },
      props: { Header: { hasBackButton: true } },
    },
    {
      path: "/imprint",
      name: "imprint",
      components: {
        default: () => import("@/views/ImprintView.vue"),
        Header: () => import("@/components/header/Header.vue"),
        Footer: () => import("@/components/footer/MainFooter.vue"),
      },
      meta: { title: "imprint" },
      props: { Header: { hasBackButton: true } },
    },
    {
      path: "/verify-email",
      name: "verifyEmail",
      components: {
        default: () => import("@/views/VerifyEmail.vue"),
      },
      meta: { title: "verifyEmail" },
    },
    {
      path: "/complete-registration",
      name: "completeRegistration",
      components: {
        default: () => import("@/views/CompleteRegistration.vue"),
      },
      meta: { title: "completeRegistration" },
    },
    {
      path: "/expired-registration",
      name: "expiredRegistration",
      components: {
        default: () => import("@/views/ExpiredRegistration.vue"),
        Footer: () => import("@/components/footer/MainFooter.vue"),
      },
      meta: { title: "expiredRegistration" },
    },
    {
      path: "/feed",
      name: "notificationsFeed",
      components: {
        default: () => import("@/views/NotificationsFeed.vue"),
        Header: () => import("@/components/header/Header.vue"),
        Footer: () => import("@/components/footer/MainFooter.vue"),
      },
      meta: { title: "notificationsFeed" },
      beforeEnter: [authGuard],
    },
    {
      path: "/notification-info",
      name: "notificationsInfo",
      components: {
        default: () => import("@/views/NotificationsInfo.vue"),
        Header: () => import("@/components/header/Header.vue"),
        Footer: () => import("@/components/footer/MainFooter.vue"),
      },
      meta: { title: "notificationsInfo" },
    },
    {
      path: "/redirect",
      name: "redirect",
      component: () => import("@/views/Redirect.vue"),
    },
  ],
  scrollBehavior() {
    // always scroll to top
    return { top: 0 };
  },
});

router.beforeEach((to) => {
  document.title = getDocumentTitle(to);
});

const getDocumentTitle = (to: RouteLocationNormalizedGeneric) => {
  if (to.meta.title) {
    return `${i18n.global.t(`pageTitles.${to.meta.title}`)} | dpa-ID Account`;
  }
  return "dpa-ID | Single Sign-On";
};

watch(i18n.global.locale, () => {
  document.title = getDocumentTitle(router.currentRoute.value);
});

export default router;
